import React, {useState} from 'react';
import {AnalysisResult, PaginationInfo} from '../types';
import FeedbackButtons from './FeedbackButtons';
import {Badge} from './ui/badge';
import {Modal} from './ui/modal';
import Pagination from './ui/pagination';
import {Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow} from './ui/table';
import {Tabs} from './ui/tabs';

/** Rows per page — same as Explore's URL table (pages/Explore.tsx). */
const RESULTS_PAGE_SIZE = 10;

/** Number shape is locked by the mockup, so it must not follow the host locale. */
const NUMBER_LOCALE = 'en-US';

type ResultFilter = 'all' | 'match' | 'nomatch';

interface AnalysisResultsTableProps {
    results: AnalysisResult[];
    onFeedbackSubmitted?: (id: string, agree: boolean) => void;
}

/**
 * The Relevance Engine's Analysis Results, rendered as one shared paginated
 * table (deepcontext-8snj). Both modes — default matching (ContextAnalyzer)
 * and classification A/B (RelevanceEngineAB) — render this component, so a
 * 999-URL run pages at ten rows instead of painting a card per result.
 *
 * Structure mirrors Explore's URL table: plain-text URL cell, a narrow
 * open-in-new-tab anchor column beside it, row click opens the detail modal
 * that holds everything the old expanding card showed. Result order is the
 * caller's order — nothing here sorts.
 */
const AnalysisResultsTable: React.FC<AnalysisResultsTableProps> = ({results, onFeedbackSubmitted}) => {
    const [filter, setFilter] = useState<ResultFilter>('all');
    const [page, setPage] = useState(1);
    const [detailResult, setDetailResult] = useState<AnalysisResult | null>(null);

    const matchedCount = results.filter(r => r.isBrandSafe).length;
    const filteredResults = results.filter(r =>
        filter === 'all' ? true : filter === 'match' ? r.isBrandSafe : !r.isBrandSafe
    );

    const totalPages = Math.max(1, Math.ceil(filteredResults.length / RESULTS_PAGE_SIZE));
    // Streaming keeps growing (and filters keep shrinking) the list underneath
    // us, so clamp rather than trusting the stored page.
    const activePage = Math.min(page, totalPages);
    const pageStartIndex = (activePage - 1) * RESULTS_PAGE_SIZE;
    const pagedResults = filteredResults.slice(pageStartIndex, pageStartIndex + RESULTS_PAGE_SIZE);

    const changeFilter = (next: ResultFilter) => {
        setFilter(next);
        setPage(1);
    };

    const paginationInfo: PaginationInfo = {
        page: activePage,
        limit: RESULTS_PAGE_SIZE,
        totalCount: filteredResults.length,
        totalPages,
        hasNextPage: activePage < totalPages,
        hasPreviousPage: activePage > 1,
        nextPage: activePage < totalPages ? activePage + 1 : null,
        previousPage: activePage > 1 ? activePage - 1 : null,
    };

    const confidencePercent = (result: AnalysisResult) => Math.round(result.confidence * 100);

    /** A genuine zero is data, not a gap — only a missing value renders the dash. */
    const renderRequestsPerDay = (result: AnalysisResult) =>
        typeof result.dailyRequests === 'number'
            ? result.dailyRequests.toLocaleString(NUMBER_LOCALE)
            : '—';

    const renderMatchBadge = (result: AnalysisResult) => (
        <Badge variant={result.isBrandSafe ? 'safe' : 'risk'} className="uppercase whitespace-nowrap">
            {result.isBrandSafe ? 'Match' : 'No Match'}
        </Badge>
    );

    const renderRow = (result: AnalysisResult, absoluteIndex: number) => (
        <TableRow
            key={result.id || absoluteIndex}
            data-testid="analysis-result-row"
            className="cursor-pointer"
            onClick={() => setDetailResult(result)}
        >
            <TableCell className="text-right tabular-nums text-ink-muted">{absoluteIndex + 1}</TableCell>
            {/* URL is plain text — the whole row (including this cell) opens the
                modal. Only the open-icon cell next to it escapes to the site. */}
            <TableCell className="overflow-hidden">
                <span
                    title={result.url}
                    className="block"
                    style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0}}
                >
                    {result.url}
                </span>
            </TableCell>
            <TableCell>
                <a
                    href={result.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open ${result.url} in a new tab`}
                    title="Open in new tab"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-accent-active hover:bg-accent/15"
                    onClick={(e) => e.stopPropagation()}
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-[15px] w-[15px]">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                        <polyline points="15 3 21 3 21 9"/>
                        <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                </a>
            </TableCell>
            <TableCell className="text-right tabular-nums whitespace-nowrap text-ink-muted">
                {renderRequestsPerDay(result)}
            </TableCell>
            <TableCell>{renderMatchBadge(result)}</TableCell>
            <TableCell className="text-right tabular-nums whitespace-nowrap text-ink-muted">
                <span className="mr-2 inline-block h-1 w-10 overflow-hidden rounded-full bg-line align-middle">
                    <span
                        className="block h-full rounded-full bg-gradient-accent"
                        style={{width: `${confidencePercent(result)}%`}}
                    />
                </span>
                {confidencePercent(result)}%
            </TableCell>
        </TableRow>
    );

    const renderDetailModal = () => (
        <Modal
            open={Boolean(detailResult)}
            onClose={() => setDetailResult(null)}
            className="max-w-lg"
            title={
                detailResult ? (
                    <a
                        href={detailResult.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="break-all text-sm text-accent-active hover:opacity-80 hover:underline"
                    >
                        {detailResult.url}
                    </a>
                ) : undefined
            }
        >
            {detailResult && (
                <>
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={() => setDetailResult(null)}
                        className="absolute right-4 top-3.5 text-ink-muted transition-colors hover:text-ink"
                    >
                        ✕
                    </button>

                    <div className="mb-4 flex items-center gap-2.5">
                        {renderMatchBadge(detailResult)}
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-line">
                            <div
                                data-testid="analysis-result-confidence-meter"
                                className="h-2 rounded-full bg-gradient-accent"
                                style={{width: `${confidencePercent(detailResult)}%`}}
                            />
                        </div>
                        <span className="text-xs font-medium text-ink">{confidencePercent(detailResult)}%</span>
                    </div>

                    <div className="mb-3.5">
                        <div className="mb-1 text-xs font-semibold text-ink">Content Summary</div>
                        <div className="text-xs leading-relaxed text-ink-muted break-words">
                            {detailResult.contentSummary || 'No summary available'}
                        </div>
                    </div>

                    <div className="mb-3.5">
                        <div className="mb-1 text-xs font-semibold text-ink">Reasoning</div>
                        <div className="text-xs leading-relaxed text-ink-muted break-words">
                            {detailResult.reasoning}
                        </div>
                    </div>

                    <div className="mb-3.5">
                        <div className="mb-1 text-xs font-semibold text-ink">Keyword Blocking</div>
                        <div className="text-xs leading-relaxed text-ink-muted break-words">
                            {detailResult.keywordBlocking
                                ? detailResult.keywordBlocking.reasoning
                                : 'Keyword blocking analysis not available for this URL.'}
                        </div>
                        {detailResult.keywordBlocking && detailResult.keywordBlocking.matchedKeywords.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                                {detailResult.keywordBlocking.matchedKeywords.map((keyword, idx) => (
                                    <Badge key={idx} variant="risk">{keyword}</Badge>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="mb-3.5 text-xs text-ink-muted">
                        <span className="font-medium">Requests / day:</span>
                        <span className="ml-1">{renderRequestsPerDay(detailResult)}</span>
                    </div>

                    <FeedbackButtons
                        analysis={detailResult}
                        onFeedbackSubmitted={onFeedbackSubmitted}
                        variant="pill"
                    />
                </>
            )}
        </Modal>
    );

    return (
        <div data-testid="analysis-results-table">
            <Tabs
                className="mb-3"
                aria-label="Filter analysis results"
                value={filter}
                onChange={(value) => changeFilter(value as ResultFilter)}
                tabs={[
                    {value: 'all', label: `All · ${results.length.toLocaleString(NUMBER_LOCALE)}`},
                    {value: 'match', label: `Matched · ${matchedCount.toLocaleString(NUMBER_LOCALE)}`},
                    {value: 'nomatch', label: `Unmatched · ${(results.length - matchedCount).toLocaleString(NUMBER_LOCALE)}`},
                ]}
            />

            <div className="min-w-0 overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
                <div className="min-w-0 overflow-x-auto bg-surface">
                    <Table className="table-fixed">
                        <TableHead>
                            <tr>
                                <TableHeaderCell className="w-[38px] text-right">#</TableHeaderCell>
                                <TableHeaderCell>URL</TableHeaderCell>
                                <TableHeaderCell className="w-[40px]">{null}</TableHeaderCell>
                                <TableHeaderCell className="w-[110px] text-right">Req / day</TableHeaderCell>
                                <TableHeaderCell className="w-[100px]">Match</TableHeaderCell>
                                <TableHeaderCell className="w-[120px] text-right">Confidence</TableHeaderCell>
                            </tr>
                        </TableHead>
                        {pagedResults.length > 0 && (
                            <TableBody>
                                {pagedResults.map((result, localIndex) => renderRow(result, pageStartIndex + localIndex))}
                            </TableBody>
                        )}
                    </Table>
                    {pagedResults.length === 0 && (
                        <div className="p-10 text-center text-sm text-ink-muted">
                            No results match this filter.
                        </div>
                    )}
                </div>

                {totalPages > 1 && (
                    <div data-testid="analysis-results-pagination" className="flex-shrink-0 border-t border-line bg-ghost-bg px-4 py-2.5">
                        <Pagination pagination={paginationInfo} onPageChange={setPage}/>
                    </div>
                )}
            </div>

            {renderDetailModal()}
        </div>
    );
};

export default AnalysisResultsTable;
